import { SEVERITY_ORDER, type Severity } from '@/components/ui/charts';
import type { FindingListItem, ListFindingsOpts } from '@/lib/api/health-findings';
import { dateValue } from '@/lib/utils/format';

/**
 * What the Findings tab reads off the URL and the findings list (T-0244 /
 * E22 design §4; prototype `findingsTab()`). Pure, so the tab stays markup
 * and these stay tested.
 *
 * Integrity rule (E22 design §6.3): the open, critical, high and resolved
 * counts and the open findings by domain are the composite read's. The one
 * statistic worked out here is the median age of the open findings, read
 * off a single list (each finding's own `created_at`); a row's age is its
 * own dates' difference.
 */

const DAY = 86_400_000;

/** The status segment: Open (the default), Resolved, Waived, All. */
export const STATUS_FILTERS = ['open', 'resolved', 'waived', 'all'] as const;
export type StatusFilter = (typeof STATUS_FILTERS)[number];

export interface FindingsFilters {
  /** 'open' when the URL names none; any other API status passes through. */
  status: string;
  severity: string;
  domain: string;
}

/** The URL's filters, with the prototype's default of open findings. */
export function filtersFromSearch(search: {
  status?: string;
  severity?: string;
  domain?: string;
}): FindingsFilters {
  return {
    status: search.status || 'open',
    severity: search.severity ?? '',
    domain: search.domain ?? '',
  };
}

/** The list request for the filters ('all' asks for every status). */
export function listQuery(filters: FindingsFilters, limit: number): ListFindingsOpts {
  return {
    status: filters.status === 'all' ? undefined : filters.status,
    severity: filters.severity || undefined,
    health_domain_id: filters.domain || undefined,
    limit,
  };
}

function startOfDay(ms: number): number {
  const d = new Date(ms);
  return new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime();
}

/**
 * Whole calendar days a finding has been open: from the day it was raised
 * to the day it was resolved, or to today while it is open. Null when the
 * API sent no raise date.
 */
export function findingAgeDays(
  finding: Pick<FindingListItem, 'created_at' | 'resolved_at'>,
  today: Date,
): number | null {
  if (!finding.created_at) return null;
  const end = finding.resolved_at ? dateValue(finding.resolved_at) : today.getTime();
  const days = Math.round((startOfDay(end) - startOfDay(dateValue(finding.created_at))) / DAY);
  return Math.max(0, days);
}

/**
 * The median age, in days, of the open findings on one list (the KPI's
 * "days open"); null when none has a raise date. An even count takes the
 * mean of the middle two, rounded to a whole day.
 */
export function medianOpenAge(
  findings: ReadonlyArray<Pick<FindingListItem, 'status' | 'created_at' | 'resolved_at'>>,
  today: Date,
): number | null {
  const ages = findings
    .filter((f) => f.status === 'open')
    .map((f) => findingAgeDays(f, today))
    .filter((a): a is number => a !== null)
    .sort((a, b) => a - b);
  if (!ages.length) return null;
  const mid = Math.floor(ages.length / 2);
  return ages.length % 2 ? ages[mid]! : Math.round((ages[mid - 1]! + ages[mid]!) / 2);
}

/** A list severity in the chart vocabulary; null for info or an unknown one. */
export function severityOf(raw: string | null): Severity | null {
  const s = raw?.toLowerCase();
  return (SEVERITY_ORDER as string[]).includes(s ?? '') ? (s as Severity) : null;
}

/**
 * The register's order (prototype): worst severity first, then the oldest;
 * findings without a known severity or a raise date go last in their group.
 */
export function sortFindings<T extends FindingListItem>(findings: readonly T[], today: Date): T[] {
  const rank = (f: T) => {
    const s = severityOf(f.severity);
    return s ? SEVERITY_ORDER.indexOf(s) : SEVERITY_ORDER.length;
  };
  return findings
    .map((f, i) => ({ f, i, age: findingAgeDays(f, today) ?? -1 }))
    .sort((a, b) => rank(a.f) - rank(b.f) || b.age - a.age || a.i - b.i)
    .map(({ f }) => f);
}
